import { BaseMode } from './BaseMode';
import { Iframe } from '@/components/Iframe';
import { Spinner } from '@/components/Spinner';
import { UrlBuilder } from '@/core/UrlBuilder';
import type { WidgetConfig } from '@/types/config';
import type { EventBus } from '@/core/EventBus';
import type {
  UpdateIframeHeightMessage,
  UpdateIframeWidthMessage,
} from '@/types/messages';

/**
 * Inline mode - embeds widget directly in the page
 */
export class InlineMode extends BaseMode {
  private iframe: Iframe | null = null;
  private spinner: Spinner | null = null;
  private container: HTMLElement | null = null;
  private containerId: string;
  private iframeId: string;

  constructor(
    config: WidgetConfig,
    eventBus: EventBus,
    private button: HTMLButtonElement
  ) {
    super(config, eventBus);
    this.containerId = this.generateId('caredove_inline_container');
    this.iframeId = this.generateId('caredove_inline_iframe');
  }

  public render(): void {
    // Hide the button
    this.button.style.display = 'none';

    // Create container
    this.createContainer();

    // Generate URL
    const url = UrlBuilder.generateUrl(this.config);

    // Create iframe
    this.iframe = new Iframe(url, this.config, this.iframeId);
    const iframeElement = this.iframe.create();

    // Add iframe to container
    if (this.container) {
      this.container.appendChild(iframeElement);
    }

    // Setup spinner
    this.spinner = new Spinner(this.containerId);
    this.spinner.show();
    this.spinner.autoHide(4000); // Auto-hide after 4s

    // Setup iframe load handler
    this.iframe.onLoad(() => {
      this.spinner?.hide();
      this.startLocationBroadcast();
      this.startPositionTracking();

      // Focus iframe after a short delay
      setTimeout(() => {
        this.iframe?.focus();
      }, 500);
    });

    // Register PostMessage handlers
    this.registerMessageHandlers();
  }

  private createContainer(): void {
    // Get parent element or use button's parent
    const parentId = this.config.elementId;
    let parentElement: HTMLElement | null = null;

    if (parentId) {
      parentElement = document.getElementById(parentId);
    } else {
      parentElement = this.button.parentElement;
    }

    if (!parentElement) {
      console.error('[CaredoveWidget] Cannot create inline container: no parent element');
      return;
    }

    // Create container div
    const container = document.createElement('div');
    container.id = this.containerId;
    container.className = 'caredove-inline-container';

    // Apply styles
    container.style.width = this.config.width || '100%';
    container.style.border = `${this.config.borderSize}px solid ${this.config.borderColor}`;
    container.style.borderRadius = this.config.borderRadius;

    if (this.config.height) {
      container.style.height = `${parseInt(this.config.height) + 2 * this.config.borderSize}px`;
    } else {
      container.style.minHeight = `${this.config.minHeight + 2}px`;
    }

    // Append to parent
    parentElement.appendChild(container);
    this.container = container;
  }

  private registerMessageHandlers(): void {
    // Update iframe height
    this.eventBus.on('updateIframeHeight', (data) => {
      const message = data as UpdateIframeHeightMessage;
      if (this.config.variableHeight && this.iframe && this.container) {
        this.iframe.updateHeight(message.height);
        this.container.style.height = `${message.height + 2 * this.config.borderSize}px`;
      }
    });

    // Update iframe width
    this.eventBus.on('updateIframeWidth', (data) => {
      const message = data as UpdateIframeWidthMessage;
      if (this.config.variableWidth && this.iframe && this.container) {
        this.iframe.updateWidth(message.servicesWrapperWidth);
        this.container.style.width = message.servicesWrapperWidth;
        this.container.style.minWidth = message.servicesWrapperWidth;
        this.container.style.maxWidth = message.servicesWrapperWidth;
      }
    });

    // Scroll to top (after form submission)
    this.eventBus.on('scrollOnTop', (_data) => {
      setTimeout(() => {
        this.iframe?.scrollIntoView(200);
      }, 200);
    });
  }

  private startLocationBroadcast(): void {
    if (!this.iframe) return;

    const iframeElement = this.iframe.getElement();
    if (!iframeElement) return;

    // Send location every second
    const intervalId = setInterval(() => {
      if (!iframeElement.contentWindow) {
        clearInterval(intervalId);
        return;
      }

      this.eventBus.sendToIframe(iframeElement, {
        message: 'embedLocation',
        embedLocation: {
          iFrameId: this.iframeId,
          embedLocation: window.location.href,
          cdMinHeight: this.config.minHeight,
        },
      });
    }, 1000);

    // Store interval for cleanup
    (this.iframe as any)._locationInterval = intervalId;
  }

  private startPositionTracking(): void {
    if (!this.iframe) return;

    const iframeElement = this.iframe.getElement();
    if (!iframeElement) return;

    this.iframe.startPositionTracking((position) => {
      this.eventBus.sendToIframe(iframeElement, {
        message: 'embedIframePosition',
        embedIframePosition: position,
      });
    });
  }

  public cleanup(): void {
    // Clear location broadcast interval
    if (this.iframe && (this.iframe as any)._locationInterval) {
      clearInterval((this.iframe as any)._locationInterval);
    }

    this.spinner?.hide();
    this.iframe?.destroy();
    this.container?.remove();

    this.spinner = null;
    this.iframe = null;
    this.container = null;

    // Show button again
    this.button.style.display = '';
  }
}
